// Les choses à faire — lecture et écriture de `taches`.
//
// Le rangement (en retard, aujourd'hui, cette semaine) et le tri vivent dans
// @imp/shared/taches, purs et testés. Ici on ne fait que parler à la base.
//
// La table existe depuis le premier jour du CRM mais n'avait jamais servi ;
// la migration 26 lui ajoute le lien vers les utilisateurs, l'auteur et les
// index. Les lectures tolèrent qu'elle ne soit pas encore passée : `cree_par_id`
// absent ne doit pas casser l'écran.

import {
  instantEcheance,
  prioriteValide,
  statutValide,
  type PrioriteTache,
  type StatutTache,
  type Tache,
} from '@imp/shared/taches';

import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

const COLONNES =
  'id,titre,description,statut,priorite,echeance,assigne_a_utilisateur_id,context_entite_type,context_entite_id,complete_at,created_at';

interface TacheRow {
  id: string;
  titre: string;
  description: string | null;
  statut: string;
  priorite: string;
  echeance: string | null;
  assigne_a_utilisateur_id: string | null;
  context_entite_type: string | null;
  context_entite_id: string | null;
  complete_at: string | null;
  created_at: string;
}

export interface Assignable {
  id: string;
  nom: string;
}

/** Un chantier tel que le champ de choix et la liste en ont besoin. */
export interface ChantierPourTache {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  ville: string | null;
  client: string | null;
}

interface ChantierRow {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  ville: string | null;
  contacts:
    | {
        type_personne: string;
        nom: string | null;
        prenom: string | null;
        raison_sociale: string | null;
      }
    | {
        type_personne: string;
        nom: string | null;
        prenom: string | null;
        raison_sociale: string | null;
      }[]
    | null;
}

function nomContact(c: ChantierRow['contacts']): string | null {
  const x = Array.isArray(c) ? c[0] : c;
  if (!x) return null;
  if (x.type_personne === 'morale') return x.raison_sociale ?? null;
  const nom = [x.nom, x.prenom].filter((v) => v !== null && v !== '').join(' ');
  return nom === '' ? null : nom;
}

export async function listTaches(): Promise<Tache[]> {
  const [taches, gens] = await Promise.all([
    supabase
      .from('taches')
      .select(COLONNES)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
    listAssignables(),
  ]);
  if (taches.error) throw new Error(taches.error.message);
  const parId = new Map(gens.map((g) => [g.id, g.nom]));

  return ((taches.data as TacheRow[] | null) ?? []).map((r) => ({
    id: r.id,
    titre: r.titre,
    description: r.description,
    statut: statutValide(r.statut),
    priorite: prioriteValide(r.priorite),
    echeance: r.echeance,
    assigneAId: r.assigne_a_utilisateur_id,
    assigneANom:
      r.assigne_a_utilisateur_id === null ? null : (parId.get(r.assigne_a_utilisateur_id) ?? null),
    entiteType: r.context_entite_type,
    entiteId: r.context_entite_id,
    // Rempli par l'écran, qui a déjà la liste des chantiers sous la main.
    entiteLibelle: null,
    createdAt: r.created_at,
    completeAt: r.complete_at,
  }));
}

export async function listAssignables(): Promise<Assignable[]> {
  const { data, error } = await supabase
    .from('utilisateurs')
    .select('id,nom,prenom')
    .is('deleted_at', null)
    .order('prenom');
  if (error) throw new Error(error.message);
  return ((data as { id: string; nom: string; prenom: string }[] | null) ?? []).map((u) => ({
    id: u.id,
    nom: [u.prenom, u.nom]
      .filter((v) => v !== '')
      .join(' ')
      .trim(),
  }));
}

export async function listChantiersPourTaches(): Promise<ChantierPourTache[]> {
  const { data, error } = await supabase
    .from('chantiers')
    .select('id,numero,libelle,statut,ville,contacts(type_personne,nom,prenom,raison_sociale)')
    .is('deleted_at', null)
    .order('numero', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data as unknown as ChantierRow[] | null) ?? []).map((c) => ({
    id: c.id,
    numero: c.numero,
    libelle: c.libelle,
    statut: c.statut,
    ville: c.ville,
    client: nomContact(c.contacts),
  }));
}

export interface SaisieTache {
  titre: string;
  description: string | null;
  priorite: PrioriteTache;
  /** Jour `AAAA-MM-JJ`, ou `null`. Converti en instant de midi local. */
  jourEcheance: string | null;
  assigneAId: string | null;
  chantierId: string | null;
}

export async function creerTache(s: SaisieTache, creeParId: string | null): Promise<void> {
  const ligne: Record<string, unknown> = {
    organization_id: ORG_ID,
    titre: s.titre,
    description: s.description,
    priorite: s.priorite,
    statut: 'a_faire',
    echeance: s.jourEcheance === null ? null : instantEcheance(s.jourEcheance),
    assigne_a_utilisateur_id: s.assigneAId,
    context_entite_type: s.chantierId === null ? null : 'chantier',
    context_entite_id: s.chantierId,
  };
  if (creeParId !== null) ligne.cree_par_id = creeParId;

  const { error } = await supabase.from('taches').insert(ligne);
  if (error) {
    // La migration 26 peut ne pas être passée : on réessaie sans l'auteur
    // plutôt que de refuser d'enregistrer la tâche.
    if (error.message.includes('cree_par_id')) {
      const sansAuteur = { ...ligne };
      delete sansAuteur.cree_par_id;
      const { error: err2 } = await supabase.from('taches').insert(sansAuteur);
      if (err2) throw new Error(err2.message);
      return;
    }
    throw new Error(error.message);
  }
}

export async function modifierTache(id: string, s: Partial<SaisieTache>): Promise<void> {
  const champs: Record<string, unknown> = {};
  if (s.titre !== undefined) champs.titre = s.titre;
  if (s.description !== undefined) champs.description = s.description;
  if (s.priorite !== undefined) champs.priorite = s.priorite;
  if (s.jourEcheance !== undefined) {
    champs.echeance = s.jourEcheance === null ? null : instantEcheance(s.jourEcheance);
  }
  if (s.assigneAId !== undefined) champs.assigne_a_utilisateur_id = s.assigneAId;
  if (s.chantierId !== undefined) {
    champs.context_entite_type = s.chantierId === null ? null : 'chantier';
    champs.context_entite_id = s.chantierId;
  }
  if (Object.keys(champs).length === 0) return;
  const { error } = await supabase.from('taches').update(champs).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Cocher ou décocher.
 *
 * `complete_at` suit le statut : une tâche rouverte perd sa date de fin, sans
 * quoi elle se rangerait un jour parmi les faites avec une date qui ment.
 */
export async function changerStatut(id: string, statut: StatutTache): Promise<void> {
  const { error } = await supabase
    .from('taches')
    .update({
      statut,
      complete_at: statut === 'fait' ? new Date().toISOString() : null,
    })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/** Suppression douce : la tâche reste, pour pouvoir expliquer ce qui a été décidé. */
export async function supprimerTache(id: string): Promise<void> {
  const { error } = await supabase
    .from('taches')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
